import React, { useMemo } from 'react';
import {
  ApprovalRequestStatusLevel,
  EscalationApproval,
  EscalationRequest, EscalationRequestAssignment,
} from '../../../types/approval-request';
import { useFetchEscalationRequest } from '../../../hooks/escalation-request-hooks';
import { Opportunity } from '../../../types/opportunity';
import { parseDateToMs } from '../../../support/utils';
import {
  getEscalationStatusLabel
} from './opportunityEscalationHelpers';
import OpportunityEscalationListRequestStage from './OpportunityEscalationListRequestStage';

interface OpportunityEscalationListItemProps {
  escalationId: EscalationRequest['id'];
  opportunityId: Opportunity['id'];
}

const OpportunityEscalationListItem: React.FC<OpportunityEscalationListItemProps> = ({
  escalationId,
  opportunityId,
}) => {
  const { data: escalation, isLoading, isError } = useFetchEscalationRequest(
    opportunityId,
    escalationId
  );

  const latestAssignmentByLevel = useMemo(() => {
    return (escalation?.assignments ?? []).reduce((acc, assignment) => {
      const level = assignment.assignment_type?.level ?? null

      if (!level) return acc;

      const current = acc[level];

      if (
        !current ||
        parseDateToMs(assignment?.last_updated ?? assignment?.created) >
          parseDateToMs(current?.last_updated ?? current?.created)
      ) {
        acc[level] = assignment;
      }

      return acc;
    }, {} as Record<number, EscalationRequestAssignment>);
  }, [escalation?.assignments]);

  const latestActiveApprovalByLevel = useMemo(() => {
    return (escalation?.approvals ?? [])
      .filter((approval) => {
        // Missing status should not be treated as active for list-stage derivation.
        const status = approval?.approval_request?.status?.status;
        if (status == null) return false;

        return (
          status !== ApprovalRequestStatusLevel.Cancelled &&
          status !== ApprovalRequestStatusLevel.Superseded
        );
      })
      .reduce((acc, approval) => {
        const level = approval?.target_assignment_type?.level ?? approval?.level ?? null;

        if (!level) return acc;

        const current = acc[level];

        if (
          !current ||
          parseDateToMs(approval?.last_updated ?? approval?.created) >
            parseDateToMs(current?.last_updated ?? current?.created)
        ) {
          acc[level] = approval;
        }

        return acc;
      }, {} as Record<number, EscalationApproval>);
  }, [escalation?.approvals]);

  const escalationStages = useMemo(() => {
    const levels = Array.from(
      new Set([
        ...Object.keys(latestActiveApprovalByLevel).map(Number),
        ...Object.keys(latestAssignmentByLevel).map(Number),
      ])
    ).sort((a, b) => a - b);

    return levels.map((level) => {
      const approvalEntity = latestActiveApprovalByLevel[level] ?? null;
      const assignment = latestAssignmentByLevel[level] ?? null;

      return {
        id: approvalEntity?.id
          ? `approval-${approvalEntity.id}-level-${level}`
          : `assignment-${assignment?.id ?? level}-level-${level}`,
        level,
        approval: approvalEntity?.approval_request ?? null,
        assignment,
      };
    });
  }, [latestActiveApprovalByLevel, latestAssignmentByLevel]);

  const status = getEscalationStatusLabel(
    escalation?.stage,
    escalation?.status,
    escalationStages
  );

  if (isLoading) {
    return <div className="italic text-xs py-2">Loading...</div>;
  }

  if (isError) {
    return <div className="italic text-xs py-2 text-red-600">Unable to load escalation.</div>;
  }

  if (!escalation) {
    return <div className="italic text-xs py-2 text-gray-500">Escalation unavailable.</div>;
  }

  return (
    <div key={escalation.id} className="flex flex-col py-2">
      <div className="flex flex-row justify-between border-t border-[#eee] p-1 text-sm font-medium">
        <div>
          {escalation?.product_name ??
            escalation?.product?.name ??
            'Unknown Product'}
        </div>

        <div className="flex flex-row gap-2 items-center">
          <div className="text-sm text-gray-500">{status}</div>
        </div>
      </div>

      <div className="pl-4 text-xs font-bold">Assignments</div>

      <div className="text-xs pl-4 pr-1">
        {escalationStages.map((stage) => (
          <OpportunityEscalationListRequestStage
            key={stage?.id}
            {...stage}
            escalation={escalation}
            product={escalation?.product}
          />
        ))}
      </div>
    </div>
  );
};

export default OpportunityEscalationListItem;